//v1.0
import { EventListener, SelectorElement } from './EventListener';
import { CoreEvents } from './CoreEvents';
import fastdom from 'fastdom';

export class Events {
	private static _listeners: Array<EventListener> = [];
	private static _idCount: number = 1;
	private static _ticker: PIXI.Ticker = null;
	private static _lastScrollY: number = 0;

	public static activeEvents(): Array<EventListener> {
		return Events._listeners;
	}

	public static init(): void {
		Events._addCoreListeners();
	}

	//set and event listener
	//Can be created in 2 forms
	//1 = on("click", ".ui-button", (e: MouseEvent) => {...})
	//2 = on("PAGE_LOAD", (data: any) => {...} )

	public static on(
		type: string,
		selector: SelectorElement,
		action: EventListenerOrEventListenerObject,
		tag: string
	): number;
	public static on(type: string, selector: SelectorElement, action: EventListenerOrEventListenerObject): number;
	public static on(type: string, action: Function): number;
	public static on(type: any, action: Function): number;
	public static on(
		type: string | any,
		selectorOrAction: SelectorElement | Function,
		action: EventListenerOrEventListenerObject | Function = null,
		tag: string = null
	): number {
		let typeAsString: string;

		//If an event then pull the eventname value from the class / instace:
		if (!(typeof type === 'string')) {
			let typeAsEvent: any = type as any;
			typeAsString = typeAsEvent.eventName || typeAsEvent.eventNameFromStatic;
			Events._checkValidEvent(typeAsEvent);
		} else {
			typeAsString = type;
		}

		let selectorElement = null;

		if (action === null) {
			action = selectorOrAction as Function;
		} else {
			selectorElement = selectorOrAction as SelectorElement;
		}

		if (action !== undefined && Events.match(typeAsString, selectorElement, action).length === 0) {
			let listener = new EventListener(typeAsString, action, selectorElement, tag);
			listener.id = this._idCount;
			Events._listeners.push(listener);
			this._idCount++;
			return listener.id;
		}

		return null;
	}

	public static off(type: string, selector: SelectorElement, action: EventListenerOrEventListenerObject): void;
	public static off(type: string, action: Function): void;
	public static off(type: any, action: Function): void;

	public static off(
		type: string | any,
		selectorOrAction: SelectorElement | Function,
		action: EventListenerOrEventListenerObject | Function = null
	): void {
		let typeAsString: string;

		//If an event then pull the eventname value from the class / instace:
		if (!(typeof type === 'string')) {
			let typeAsEvent: any = type as any;
			typeAsString = typeAsEvent.eventName || typeAsEvent.eventNameFromStatic;
			Events._checkValidEvent(typeAsEvent);
		} else {
			typeAsString = type;
		}

		let selectorElement = null;

		if (action === null) {
			action = selectorOrAction as Function;
		} else {
			selectorElement = selectorOrAction as SelectorElement;
		}

		let matches = Events.match(typeAsString, selectorElement, action);

		for (let i = 0; i < matches.length; i++) {
			matches[i].destroy();
		}

		Events._purge();
	}

	public static offById(...ids: Array<number>): void {
		for (let i = 0; i < Events._listeners.length; i++) {
			if (ids.indexOf(Events._listeners[i].id) !== -1) {
				Events._listeners[i].destroy();
			}
		}
		Events._purge();
	}

	public static offByTag(tag: string): void {
		let tagged: Array<EventListener> = Events._listeners.filter((listener: EventListener) => {
			return listener.tag === tag;
		});
		tagged.forEach((listener: EventListener) => listener.destroy());
		Events._purge();
	}

	public static dispatch(event: string | any, data: any = null, settings: any = null): void {
		let typeAsString: string;
		let dataAsAny: any;

		//If an event then pull the eventname value from the class / instace:
		if (!(typeof event === 'string')) {
			let typeAsEvent: any = event as any;
			typeAsString = typeAsEvent.eventName || typeAsEvent.eventNameFromStatic;
			dataAsAny = typeAsEvent;
			Events._checkValidEvent(typeAsEvent);

			//Switch settings with data (as the event contains the params for the event)
			settings = data;
		} else {
			typeAsString = event as string;
			dataAsAny = data;
		}

		var matches: EventListener[] = this.match(typeAsString, null);
		matches.forEach(function (match: EventListener) {
			match.dispatch(dataAsAny, settings);
		});
	}

	public static match(
		type: string,
		selector: SelectorElement = null,
		action: EventListenerOrEventListenerObject | Function = null
	): Array<EventListener> {
		return Events._listeners.filter((listener) => listener.match(type, selector, action));
	}

	public static ready(callback: Function): void {
		Events.on(CoreEvents.READY, callback);
	}

	public static tick(callback: Function): void {
		Events.on(CoreEvents.TICK, callback);
	}

	public static DOMUpdate(callback: Function): void {
		Events.on(CoreEvents.DOM_UPDATED, callback);
	}

	static scroll(callback: Function): void {
		Events.on(CoreEvents.SCROLL, callback);
	}

	static resize(callback: Function): void {
		Events.on(CoreEvents.RESIZE, callback);
	}

	//Private methods
	//-------------------------------------------------------------------------------------------
	//-------------------------------------------------------------------------------------------
	//-------------------------------------------------------------------------------------------
	//-------------------------------------------------------------------------------------------
	private static _addCoreListeners(): void {
		this._ready();
		this._scroll();
		this._resize();
		this._tick();
	}

	private static _ready(): void {
		document.onreadystatechange = () => {
			if (document.readyState === 'interactive') {
				Events.dispatch(CoreEvents.READY);
				//Destroy ready listeners:
				Events.match(CoreEvents.READY).forEach((listener: EventListener) => listener.destroy());
				//Purge:
				Events._purge();
			}
		};
	}

	private static _scroll(): void {
		window.addEventListener('scroll', (e) => {
			fastdom.measure(() => {
				this._lastScrollY = window.scrollY;
			});

			Events.dispatch(CoreEvents.SCROLL, this._lastScrollY);
		});
	}

	private static _resize(): void {
		let queId = null;

		window.addEventListener('resize', (e) => {
			fastdom.clear(queId);
			queId = fastdom.measure(() => {
				Events.dispatch(CoreEvents.RESIZE, { width: window.innerWidth, height: window.innerHeight });

				queId = null;
			});
		});

		window.addEventListener('orientationchange', () => {
			fastdom.clear(queId);
			queId = fastdom.measure(() => {
				Events.dispatch(CoreEvents.RESIZE, { width: window.innerWidth, height: window.innerHeight });
				queId = null;
			});

			setTimeout(() => {
				fastdom.clear(queId);
				queId = fastdom.measure(() => {
					Events.dispatch(CoreEvents.RESIZE, { width: window.innerWidth, height: window.innerHeight });
					queId = null;
				});
			}, 500);
		});
	}

	private static _tick(): void {
		Events._ticker = PIXI.Ticker.shared;
		Events._ticker.add(this._dotick.bind(this));
	}

	private static _dotick(delta = 1): void {
		try {
			Events.dispatch(CoreEvents.TICK, { delta: delta, deltaMs: Events._ticker.deltaMS });
		} catch (e) {
			console.error(e);
		}
	}

	private static _purge(): void {
		Events._listeners = Events._listeners.filter((listener) => listener.type);
	}

	/**
	 * Check a valid event setup (by checking has eventName set)
	 * @param {DebugEvent} event
	 * @throws {Error}
	 */
	private static _checkValidEvent(event: any): void {
		let eventName: string = event.eventName || event.eventNameFromStatic;
		if (!eventName || eventName === '') {
			try {
				let name: string = event.name || event.constructor.name;
				throw new Error(`Event class ${name} has no static 'eventName' set`);
			} catch (e) {
				console.log(e);
			}
		}
	}
}
